import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Floating Telegram Button Component
 * Shows a floating button for instant Telegram orders/support
 * Hidden on admin pages
 */
const TelegramFloatingButton = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();

  const telegramBotUrl = 'https://t.me/mydummyticket_bot';

  // Hide on admin pages
  const isAdminPage = location.pathname.includes('/admin') || 
                      location.pathname.includes('/mdt-secure-portal');
  
  if (isAdminPage) {
    return null;
  }

  const handleClick = () => {
    window.open(telegramBotUrl, '_blank');
  };

  return (
    <>
      {/* Floating Button Container */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '12px',
        }}
      >
        {/* Expanded Menu */}
        {isExpanded && (
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              padding: '16px',
              marginBottom: '8px',
              animation: 'slideUp 0.3s ease-out',
              minWidth: '220px',
            }}
          >
            <div style={{ marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#1a365d', fontWeight: '600' }}>
                Chat with us on Telegram
              </h4>
              <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                Instant orders & 24/7 support
              </p>
            </div>
            
            <button
              onClick={handleClick}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #0088cc 0%, #0077b5 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 136, 204, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              Start Chat
            </button>
            
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: '#e8f5e9',
                color: '#2e7d32',
                borderRadius: '4px',
              }}>
                ✓ Instant Order
              </span>
              <span style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: '#e3f2fd',
                color: '#1565c0',
                borderRadius: '4px',
              }}>
                ✓ 1 Min Delivery
              </span>
              <span style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: '#fff3e0',
                color: '#e65100',
                borderRadius: '4px',
              }}>
                ✓ 24/7 Support
              </span>
            </div>
          </div>
        )}

        {/* Main Floating Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          data-testid="telegram-floating-btn"
          style={{
            width: isHovered ? '180px' : '60px',
            height: '60px',
            borderRadius: '30px',
            background: 'linear-gradient(135deg, #0088cc 0%, #0077b5 100%)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isHovered ? 'flex-start' : 'center',
            padding: isHovered ? '0 20px' : '0',
            gap: '10px',
            boxShadow: '0 4px 20px rgba(0, 136, 204, 0.4)',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
          }}
        >
          {/* Telegram Icon */}
          <svg 
            width="28" 
            height="28" 
            viewBox="0 0 24 24" 
            fill="white"
            style={{ flexShrink: 0 }}
          >
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
          
          {/* Expanded Text */}
          <span
            style={{
              color: 'white',
              fontWeight: '600',
              fontSize: '14px',
              whiteSpace: 'nowrap',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s ease',
            }}
          >
            Order Now
          </span>
        </button>

        {/* Pulse Animation Ring */}
        {!isExpanded && (
          <div
            style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              border: '2px solid #0088cc',
              animation: 'pulse 2s infinite',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.3);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default TelegramFloatingButton;
